import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Heart, CheckCircle, AlertCircle } from 'lucide-react';
import { publicReferralSchema, type PublicReferralFormData } from '../schemas/referralSchema';
import { referralService } from '../api/referrals';

export default function PublicReferralForm() {
  const { code } = useParams<{ code: string }>();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [referrerInfo, setReferrerInfo] = useState<any>(null);
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<PublicReferralFormData>({
    resolver: zodResolver(publicReferralSchema),
  });

  useEffect(() => {
    if (code) {
      loadReferrerInfo();
    }
  }, [code]);

  const loadReferrerInfo = async () => {
    try {
      const resolved = await referralService.resolveReferrerByCode(code!);
      if (resolved) {
        setReferrerInfo(resolved);
      } else {
        setError('Invalid referral code');
      }
    } catch (err: any) {
      setError('Failed to load referrer information');
    }
  };

  const onSubmit = async (data: PublicReferralFormData) => {
    if (!code) return;

    try {
      setLoading(true);
      setError('');
      
      await referralService.createPublicReferral(code, data);
      setSubmitted(true);
      reset();
    } catch (err: any) {
      setError(err.message || 'Failed to submit referral');
    } finally {
      setLoading(false);
    }
  };

  if (!code) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Invalid Link</h2>
            <p className="text-gray-600">This referral link is not valid.</p>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
          <div className="text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Referral Submitted</h2>
            <p className="text-gray-600 mb-4">
              Thank you! Your referral has been submitted successfully. 
              We will contact you soon to schedule an appointment.
            </p>
            <button
              onClick={() => {
                setSubmitted(false);
                reset();
              }}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Submit Another Referral
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Heart className="w-12 h-12 text-blue-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900">Patient Referral</h2>
          {referrerInfo && (
            <p className="mt-2 text-sm text-gray-600">
              Referred by: <span className="font-medium">{referrerInfo.entity.name}</span>
            </p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex">
                <AlertCircle className="w-5 h-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label htmlFor="patientName" className="block text-sm font-medium text-gray-700">
                Patient Name *
              </label>
              <input
                {...register('patientName')}
                type="text"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter patient's full name"
              />
              {errors.patientName && (
                <p className="mt-1 text-sm text-red-600">{errors.patientName.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="patientPhone" className="block text-sm font-medium text-gray-700">
                Phone Number *
              </label>
              <input
                {...register('patientPhone')}
                type="tel"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter phone number"
              />
              {errors.patientPhone && (
                <p className="mt-1 text-sm text-red-600">{errors.patientPhone.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="toSpecialty" className="block text-sm font-medium text-gray-700">
                Specialty (Optional)
              </label>
              <select
                {...register('toSpecialty')}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select specialty</option>
                <option value="cardiology">Cardiology</option>
                <option value="dermatology">Dermatology</option>
                <option value="endocrinology">Endocrinology</option>
                <option value="gastroenterology">Gastroenterology</option>
                <option value="neurology">Neurology</option>
                <option value="orthopedics">Orthopedics</option>
                <option value="pediatrics">Pediatrics</option>
                <option value="psychiatry">Psychiatry</option>
                <option value="general">General Medicine</option>
              </select>
            </div>

            <div>
              <label htmlFor="reason" className="block text-sm font-medium text-gray-700">
                Reason for Referral *
              </label>
              <textarea
                {...register('reason')}
                rows={3}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Describe the reason for this referral"
              />
              {errors.reason && (
                <p className="mt-1 text-sm text-red-600">{errors.reason.message}</p>
              )}
            </div>

            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  {...register('consentGiven')}
                  type="checkbox"
                  className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="consentGiven" className="font-medium text-gray-700">
                  I consent to this referral *
                </label>
                <p className="text-gray-500">
                  I agree to share my information for the purpose of this medical referral.
                </p>
              </div>
            </div>
            {errors.consentGiven && (
              <p className="text-sm text-red-600">{errors.consentGiven.message}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Submitting...' : 'Submit Referral'}
            </button>
          </form>

          <div className="mt-6 text-center text-xs text-gray-500">
            <p>This form is secure and your information is protected.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
